import { cloneDeep } from '@apollo/client/utilities';
import { AutoFixHigh } from '@mui/icons-material';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import {
    EOpportunityRequestTypes,
    type IOpportunityNew,
    type TOpportunityRequestingState,
} from '@sharedInterfaces/IOpportunity';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CertificatesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CertificatesInput/CertificatesInput';
import CompetencesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CompetencesInput/CompetencesInput';
import CurrencyInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CurrencyInput/CurrencyInput';
import DateInput from '@src/App/Components/General/FormularElements/formsControls/inputs/DateInput/DateInput';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import LinkInput from '@src/App/Components/General/FormularElements/formsControls/inputs/LinkInput/LinkInput';
import NumberInput from '@src/App/Components/General/FormularElements/formsControls/inputs/NumberInput/NumberInput';
import OpportunityStatusSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/OpportunityStatusSelect/OpportunityStatusSelect';
import PercentInput from '@src/App/Components/General/FormularElements/formsControls/inputs/PercentInput/PercentInput';
import RoleSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/RoleSelect/RoleSelect';
import RolesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/RolesInput/RolesInput';
import SkillsInput from '@src/App/Components/General/FormularElements/formsControls/inputs/SkillsInput/SkillsInput';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import styles from './OpportunityForm.module.css';

interface OpporunityFormProps {
    opportunity: IOpportunityNew;
    requestingState: TOpportunityRequestingState;
    requestAnalyse: (type: EOpportunityRequestTypes | EOpportunityRequestTypes[]) => void;
    setOpportunity: (op: (prev: IOpportunityNew) => IOpportunityNew) => void;
}
/**
 * OpporunityForm component.
 *
 * @param {object} opportunity - The opportunity object.
 * @param {function} setOpportunity - The function to update the opportunity object.
 * @returns {JSX.Element} - The rendered OpportunityForm component.
 */
export function OpporunityForm({ opportunity, requestingState, setOpportunity, requestAnalyse }: OpporunityFormProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;

    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);
    const allRoles = useSelector((state: AppState) => state.company.allRoles);

    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);

    const handleChange = (field: string, value: unknown) => {
        // console.log('handleChange', field, value);
        setOpportunity((prevOpportunity) => ({
            // ...cloneDeep(prevOpportunity),
            ...prevOpportunity,
            [field]: value,
        }));
    };

    return (
        <Stack spacing={3} sx={{ p: 1 }}>
            <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: 'primary.main',
                        mb: 2,
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        bgcolor: 'background.default',
                        px: 1,
                    }}
                >
                    Grunddaten
                </Typography>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.opportunityName}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <TextInput
                                size="medium"
                                value={opportunity.title}
                                onChange={(title) => handleChange('title', title)}
                                disabled={requestingState.title}
                                helperText={requestingState.title ? langStrings.oftenUsed.generating : undefined}
                            />
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.externalID}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <TextInput
                                    size="small"
                                    value={opportunity.externalId}
                                    onChange={(externalId) => handleChange('externalId', externalId)}
                                    disabled={requestingState.externalId}
                                    helperText={
                                        requestingState.externalId ? langStrings.oftenUsed.generating : undefined
                                    }
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.status}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <OpportunityStatusSelect
                                    value={opportunity.status}
                                    onChange={(status) => handleChange('status', status)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.title}
                    sx={{ mt: 1 }}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.title,
                        EOpportunityRequestTypes.externalId,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>
            <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: 'primary.main',
                        mb: 2,
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        bgcolor: 'background.default',
                        px: 1,
                    }}
                >
                    Zeitplanung
                </Typography>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.startDate}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <DateInput
                                    value={opportunity.projectStart}
                                    disabled={requestingState.projectDates}
                                    helperText={
                                        requestingState.projectDates ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(projectStart) => handleChange('projectStart', projectStart)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.endDate}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <DateInput
                                    value={opportunity.projectEnd}
                                    disabled={requestingState.projectDates}
                                    helperText={
                                        requestingState.projectDates ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(projectEnd) => handleChange('projectEnd', projectEnd)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.publishDate}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <DateInput
                                    value={opportunity.publishDate}
                                    disabled={requestingState.projectDates}
                                    helperText={
                                        requestingState.projectDates ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(publishDate) => handleChange('publishDate', publishDate)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.deadline}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <DateInput
                                    value={opportunity.deadline}
                                    disabled={requestingState.projectDates}
                                    helperText={
                                        requestingState.projectDates ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(deadline) => handleChange('deadline', deadline)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.projectDates}
                    sx={{ mt: 1 }}
                    onClick={requestAnalyse.bind(null, EOpportunityRequestTypes.projectDates)}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>
            <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: 'primary.main',
                        mb: 2,
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        bgcolor: 'background.default',
                        px: 1,
                    }}
                >
                    Projektdetails
                </Typography>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.utilization}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <PercentInput
                                    value={opportunity.utilization}
                                    disabled={requestingState.projectNumbers}
                                    helperText={
                                        requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(utilization) => handleChange('utilization', utilization)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.remote}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <PercentInput
                                    value={opportunity.remote}
                                    disabled={requestingState.projectNumbers}
                                    helperText={
                                        requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(remote) => handleChange('remote', remote)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.employees}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <NumberInput
                                    value={opportunity.headCount}
                                    disabled={requestingState.projectNumbers}
                                    helperText={
                                        requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(headCount) => handleChange('headCount', headCount)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.dayPrice}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <CurrencyInput
                                    value={opportunity.dayPrice}
                                    disabled={requestingState.projectNumbers}
                                    helperText={
                                        requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(dayPrice) => handleChange('dayPrice', dayPrice)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.location}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <TextInput
                                size="small"
                                value={opportunity.loc}
                                disabled={requestingState.location}
                                helperText={requestingState.location ? langStrings.oftenUsed.generating : undefined}
                                onChange={(loc) => handleChange('loc', loc)}
                            />
                        </Box>
                    </Box>
                </Stack>
                <IconButton
                    disabled={requestingState.projectNumbers}
                    sx={{ mt: 1 }}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.projectNumbers,
                        EOpportunityRequestTypes.location,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>
            <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: 'primary.main',
                        mb: 2,
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        bgcolor: 'background.default',
                        px: 1,
                    }}
                >
                    Kunde & Quelle
                </Typography>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.customer}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <TextInput
                                    size="small"
                                    value={opportunity.customer}
                                    disabled={requestingState.customerPartner}
                                    helperText={
                                        requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(customer) => handleChange('customer', customer)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.partner}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <TextInput
                                    size="small"
                                    value={opportunity.partner}
                                    disabled={requestingState.customerPartner}
                                    helperText={
                                        requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(partner) => handleChange('partner', partner)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.sourceName}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <TextInput
                                    size="small"
                                    value={opportunity.sourceName}
                                    disabled={requestingState.customerPartner}
                                    helperText={
                                        requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(sourceName) => handleChange('sourceName', sourceName)}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.sourceURL}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <LinkInput
                                    size="small"
                                    value={opportunity.sourceURL}
                                    disabled={requestingState.customerPartner}
                                    helperText={
                                        requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                    }
                                    onChange={(sourceURL) => handleChange('sourceURL', sourceURL)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.customerPartner}
                    sx={{ mt: 1 }}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.customerPartner,
                        EOpportunityRequestTypes.source,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>
            {readRoles && (
                <Paper
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'primary.main',
                            mb: 2,
                            position: 'absolute',
                            top: -8,
                            left: 16,
                            bgcolor: 'background.default',
                            px: 1,
                        }}
                    >
                        Rollen
                    </Typography>
                    <Stack spacing={2} sx={{ flexGrow: 1 }}>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.primaryRole}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <RoleSelect
                                    selectedRole={opportunity.primaryRole}
                                    setSelectedRole={(role) => handleChange('primaryRole', role)}
                                    disabled={requestingState.roles}
                                    allowNew
                                    helperText={requestingState.roles ? langStrings.oftenUsed.generating : undefined}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                                {langStrings.oftenUsed.roles}:
                            </Typography>
                            <Box sx={{ mt: 0 }}>
                                <RolesInput
                                    allRoles={allRoles}
                                    selectedRoles={opportunity.roles}
                                    showMode={false}
                                    size={'medium'}
                                    disabled={requestingState.roles}
                                    helperText={requestingState.roles ? langStrings.oftenUsed.generating : undefined}
                                    onChange={(roles) => handleChange('roles', roles)}
                                />
                            </Box>
                        </Box>
                    </Stack>
                    <IconButton
                        disabled={requestingState.roles}
                        sx={{ mt: 1 }}
                        onClick={requestAnalyse.bind(null, EOpportunityRequestTypes.roles)}
                    >
                        <AutoFixHigh />
                    </IconButton>
                </Paper>
            )}
            <Paper
                variant="outlined"
                sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, position: 'relative' }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        color: 'primary.main',
                        mb: 2,
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        bgcolor: 'background.default',
                        px: 1,
                    }}
                >
                    Anforderungen
                </Typography>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.oftenUsed.competences}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <CompetencesInput
                                showMode={false}
                                size="medium"
                                competences={cloneDeep(opportunity.competences)}
                                disabled={requestingState.competences}
                                helperText={requestingState.competences ? langStrings.oftenUsed.generating : undefined}
                                onChange={(competences) => handleChange('competences', competences)}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            Benötigte Fähigkeiten:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <SkillsInput
                                size="medium"
                                showMode={false}
                                allowNewSkills={true}
                                skills={opportunity.skills}
                                allSkills={allSkills}
                                competences={opportunity.competences}
                                disabled={requestingState.skills}
                                helperText={requestingState.skills ? langStrings.oftenUsed.generating : undefined}
                                onChange={(skills) => handleChange('skills', skills)}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                            {langStrings.oftenUsed.certificates}:
                        </Typography>
                        <Box sx={{ mt: 0 }}>
                            <CertificatesInput
                                allCertificates={allCertificates}
                                selectedCertificates={opportunity.certificates}
                                showMode={false}
                                size="medium"
                                competences={opportunity.competences}
                                disabled={requestingState.certificates}
                                helperText={requestingState.certificates ? langStrings.oftenUsed.generating : undefined}
                                onChange={(certificates) =>
                                    handleChange(
                                        'certificates',
                                        certificates.map((s) => ({ title: s.title, id: s.id })),
                                    )
                                }
                            />
                        </Box>
                    </Box>
                </Stack>
                <IconButton
                    disabled={requestingState.competences || requestingState.skills || requestingState.certificates}
                    sx={{ mt: 1 }}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.competences,
                        EOpportunityRequestTypes.skills,
                        EOpportunityRequestTypes.certificates,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>

            {/* <Row>
                <RowElement alignTitle={'center'} title={langStrings.selectedEmployee}>
                    <EmployeesInput selectedEmployees={opportunity.selectedEmployees} allEmployees={allEmployees}
                        onChange={emps =>
                        {
                            setOpportunity({
                                ...opportunity,
                                selectedEmployees: emps.map(emp => ({
                                    id: emp.id,
                                    title: emp.title,
                                    skillCoverage: 0,
                                    score: 0,
                                    missingSkills: [],
                                    coveredSkills: [],
                                }))
                            });
                        }} showMode={false} size={'medium'} />
                </RowElement>
            </Row> */}
            <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {langStrings.opportunity}:
                </Typography>
                <Box sx={{ mt: 0 }}>
                    <FormatedTextInput
                        isAI
                        value={opportunity.requestText}
                        onChange={(requestText) => handleChange('requestText', requestText)}
                    />
                </Box>
            </Box>
        </Stack>
    );
}
